import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type { AgendaEvent } from "@domaddress/shared";
import { isRecurring } from "../lib/recurrenceLabel";

export default function EventsPage() {
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [form, setForm] = useState({
    title: "",
    start_datetime: "",
    end_datetime: "",
    description: "",
    is_public: true,
    sync_to_wordpress: false,
    location_name: "",
  });

  const load = useCallback(async () => {
    const res = await api.get<{ data: AgendaEvent[] }>("/api/events");
    setEvents(res.data.data);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await api.post("/api/events", {
      title: form.title,
      start_datetime: form.start_datetime.replace("T", " "),
      end_datetime: form.end_datetime.replace("T", " "),
      description: form.description || null,
      is_public: form.is_public,
      sync_to_wordpress: form.sync_to_wordpress,
      location_name: form.location_name || null,
      recurrence_type: "none",
    });
    setForm({
      title: "",
      start_datetime: "",
      end_datetime: "",
      description: "",
      is_public: true,
      sync_to_wordpress: false,
      location_name: "",
    });
    await load();
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Evenementen</h1>
          <p className="page-subtitle">
            Logistiek hier; liturgische details (spreker, PDF&apos;s) blijven in WordPress DomEvents.
          </p>
        </div>
      </div>

      <div className="card">
        <h2>Nieuw evenement</h2>
        <form className="agenda-form" onSubmit={(e) => void handleSubmit(e)}>
          <div className="form-row">
            <label>Titel</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="form-row">
            <label>Start</label>
            <input
              type="datetime-local"
              required
              value={form.start_datetime}
              onChange={(e) => setForm({ ...form, start_datetime: e.target.value })}
            />
          </div>
          <div className="form-row">
            <label>Einde</label>
            <input
              type="datetime-local"
              required
              value={form.end_datetime}
              onChange={(e) => setForm({ ...form, end_datetime: e.target.value })}
            />
          </div>
          <div className="form-row">
            <label>Locatie (tekst)</label>
            <input value={form.location_name} onChange={(e) => setForm({ ...form, location_name: e.target.value })} />
          </div>
          <div className="form-row">
            <label>
              <input
                type="checkbox"
                checked={form.is_public}
                onChange={(e) => setForm({ ...form, is_public: e.target.checked })}
              />{" "}
              Publiek op agenda
            </label>
          </div>
          <div className="form-row">
            <label>
              <input
                type="checkbox"
                checked={form.sync_to_wordpress}
                onChange={(e) => setForm({ ...form, sync_to_wordpress: e.target.checked })}
              />{" "}
              Sync naar WordPress (dom_event)
            </label>
          </div>
          <button type="submit" className="btn-primary">
            Opslaan
          </button>
        </form>
        <p style={{ fontSize: "0.85rem", color: "var(--text-500)", marginTop: "1rem" }}>
          Publieke ICS (Option C):{" "}
          <code>{import.meta.env.VITE_API_URL ?? ""}/api/events/public.ics</code>
        </p>
      </div>

      <h2 className="section-title" style={{ marginTop: "8px" }}>Evenementen</h2>
      {events.map((ev) => (
        <Link key={ev.id} to={`/evenementen/${ev.id}`} className="card card-link">
          <strong>{ev.title}</strong>
          {isRecurring(ev) && (
            <span className="event-recurrence-badge" title="Terugkerend">
              {" "}
              ↻
            </span>
          )}
          {ev.sync_to_wordpress && <span> · WP</span>}
          {(ev.rooms?.length ?? 0) > 0 && (
            <span className="event-room-badge">
              {" "}
              · {(ev.rooms ?? []).map((r) => r.room_name ?? `Zaal ${r.room_id}`).join(", ")}
            </span>
          )}
          {(ev.required_roles?.length ?? 0) > 0 && (
            <span className="event-roster-badge">
              {" "}
              · {ev.required_roles!.length} taak{ev.required_roles!.length === 1 ? "" : "en"}
            </span>
          )}
          <br />
          <small>
            {ev.start_datetime} — {ev.location_name ?? "—"}
          </small>
          <span className="card-link-hint">Rooster beheren →</span>
        </Link>
      ))}
    </div>
  );
}
